import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Pencil, ExternalLink } from "lucide-react";
import { useAdmin } from "../lib/admin";

const COLOR_DOT = {
  "Auburn": "#8B3A2A",
  "Blonde": "#E8C98A",
  "Burgundy": "#5C1A2B",
  "Copper": "#B87333",
  "Dark Brown": "#3B2414",
  "Honey Blonde": "#D8A663",
  "Jet Black": "#0B0B0B",
  "Natural Black": "#1A1410",
  "Platinum": "#E8E6DF",
};

const ProductCard = ({ product, onQuickView, onEdit }) => {
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const { isAdmin } = useAdmin();
  const [hovered, setHovered] = useState(false);

  const open = (e) => {
    e.preventDefault();
    onQuickView?.(product);
  };

  return (
    <article
      data-testid={`product-card-${product.handle}`}
      className="group fade-up relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isAdmin && (
        <button
          data-testid={`product-edit-${product.handle}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEdit?.(product);
          }}
          className="absolute z-20 top-3 right-3 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-sm px-3 py-2 shadow-lg ring-2 ring-[#D4A574] hover:bg-[hsl(var(--primary))]/90 transition-all text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5"
          aria-label={`Edit ${product.title}`}
          title="Edit price & details"
        >
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      )}

      <button
        type="button"
        onClick={open}
        data-testid={`product-link-${product.handle}`}
        className="block text-left w-full"
      >
        <div className="relative overflow-hidden bg-secondary rounded-sm aspect-[4/5]">
          {product.image ? (
            <img
              src={product.image}
              alt={product.title}
              loading="lazy"
              className="img-zoom w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center text-muted-foreground p-4 gap-1">
              <div className="text-[10px] uppercase tracking-widest">Photo coming soon</div>
              <div className="text-[10px] opacity-70">{product.title}</div>
            </div>
          )}
          {onSale && (
            <Badge
              data-testid={`product-sale-badge-${product.handle}`}
              className="absolute top-3 left-3 rounded-sm bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] uppercase tracking-widest text-[10px] font-semibold"
            >
              Sale
            </Badge>
          )}
          <div
            className={`absolute bottom-3 right-3 transition-opacity ${hovered ? "opacity-100" : "opacity-0"}`}
          >
            <span className="inline-flex items-center gap-1 bg-white/95 text-foreground text-xs px-3 py-1.5 rounded-sm">
              Quick view <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </div>

        <div className="pt-4 pb-2">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-serif text-lg lg:text-xl leading-tight text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
              {product.title}
            </h3>
            <div className="text-right shrink-0">
              {onSale && (
                <div className="text-xs text-muted-foreground line-through">
                  ${product.compare_at_price?.toFixed(2)}
                </div>
              )}
              <div className={`text-base font-semibold ${onSale ? "text-[hsl(var(--primary))]" : "text-foreground"}`}>
                ${product.price?.toFixed(2)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                + tax at checkout
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {product.length && (
              <span data-testid={`product-length-${product.handle}`} className="uppercase tracking-wider">
                {product.length}
              </span>
            )}
            {product.lace_type && (
              <>
                <span className="text-border">•</span>
                <span data-testid={`product-lace-${product.handle}`} className="uppercase tracking-wider">
                  {product.lace_type}
                </span>
              </>
            )}
            {product.color && (
              <>
                <span className="text-border">•</span>
                <span data-testid={`product-color-${product.handle}`} className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full border border-border"
                    style={{ background: COLOR_DOT[product.color] || "#888" }}
                    aria-hidden
                  />
                  {product.color}
                </span>
              </>
            )}
            {typeof product.stock === "number" && (
              <>
                {(product.length || product.lace_type || product.color) && (
                  <span className="text-border">•</span>
                )}
                <span
                  data-testid={`product-stock-${product.handle}`}
                  className={`uppercase tracking-wider ${
                    product.stock === 0
                      ? "text-[hsl(var(--primary))]"
                      : product.stock <= 5
                      ? "text-[hsl(var(--primary))] font-semibold"
                      : ""
                  }`}
                >
                  {product.stock === 0
                    ? "Sold out"
                    : product.stock <= 5
                    ? `Only ${product.stock} left`
                    : `${product.stock} in stock`}
                </span>
              </>
            )}
          </div>
        </div>
      </button>
    </article>
  );
};

export default ProductCard;
